import { useState, useMemo, type ReactNode } from 'react'
import { useNls } from '../../../nls/nlsProvider'
import { useRoles } from '../context/RolesContext'
import { usePlanLimitOverrides } from '../../../services/planLimitsService'
import {
  TIER_IDS,
  FEATURE_KEYS,
  WORKSPACE_COUNT_LIMIT_KEYS,
  resolveTierLimit,
  resolveTeamWorkspaceCaps,
  resolveTeamWorkspaceDefaults,
  validatePlanLimitOverrides,
  type TierId,
  type FeatureKey,
  type WorkspaceCountLimitKey,
  type PlanLimitOverrides,
} from '../../../config/planLimitsCore'
import { WORKSPACE_LIMIT_HARD_CAPS } from '../../../config/workspaceLimitCaps'
import {
  buildMatrix,
  buildTierPayload,
  type TierDraft,
  type MatrixRow,
} from './ownerPlanLimitsModel'
import {
  previewPlanLimits,
  applyPlanLimits,
  type PlanLimitsPreview,
} from '../../../services/ownerConsoleService'
import { navigateOwner } from '../ownerRouter'
import { Button } from '../../Common/Button'
import { Toggle } from '../../Common/Toggle'
import { Select } from '../../Common/Select'
import { TextInput } from '../../Common/TextInput'
import { Textarea } from '../../Common/Textarea'
import { OwnerTable, type ColumnDef } from '../kit/OwnerTable'
import { type STRINGS_NLS } from '../../../nls/strings_nls'
import './OwnerPlanLimitsPanel.css'

const FEATURE_KEY_LABELS: Record<FeatureKey, keyof typeof STRINGS_NLS> = {
  workspaces: 'NLSOwnerConsolePlanLimitsKey_workspaces',
  storageGB: 'NLSOwnerConsolePlanLimitsKey_storageGB',
  chatHistoryDays: 'NLSOwnerConsolePlanLimitsKey_chatHistoryDays',
  chatMessageLimit: 'NLSOwnerConsolePlanLimitsKey_chatMessageLimit',
  activeIntegrations: 'NLSOwnerConsolePlanLimitsKey_activeIntegrations',
  meetingsPerMonth: 'NLSOwnerConsolePlanLimitsKey_meetingsPerMonth',
  webhookCallsPerMonth: 'NLSOwnerConsolePlanLimitsKey_webhookCallsPerMonth',
  auditLogRetentionDays: 'NLSOwnerConsolePlanLimitsKey_auditLogRetentionDays',
  syncSpeedSeconds: 'NLSOwnerConsolePlanLimitsKey_syncSpeedSeconds',
  aiChatHistories: 'NLSOwnerConsolePlanLimitsKey_aiChatHistories',
  aiContextAttachments: 'NLSOwnerConsolePlanLimitsKey_aiContextAttachments',
  maxSavedCharts: 'NLSOwnerConsolePlanLimitsKey_maxSavedCharts',
  whatsappMonthlyCap: 'NLSOwnerConsolePlanLimitsKey_whatsappMonthlyCap',
}

const WORKSPACE_KEY_LABELS: Record<WorkspaceCountLimitKey, keyof typeof STRINGS_NLS> = {
  maxGoalsPerBoard: 'NLSOwnerConsolePlanLimitsKey_maxGoalsPerBoard',
  maxTasksPerBoard: 'NLSOwnerConsolePlanLimitsKey_maxTasksPerBoard',
  maxTasksPerGoal: 'NLSOwnerConsolePlanLimitsKey_maxTasksPerGoal',
  maxSubtasksPerBoard: 'NLSOwnerConsolePlanLimitsKey_maxSubtasksPerBoard',
  maxSubtasksPerTask: 'NLSOwnerConsolePlanLimitsKey_maxSubtasksPerTask',
  maxChecklistsPerBoard: 'NLSOwnerConsolePlanLimitsKey_maxChecklistsPerBoard',
  maxChecklistsPerContainer: 'NLSOwnerConsolePlanLimitsKey_maxChecklistsPerContainer',
  maxWorkspaceMembers: 'NLSOwnerConsolePlanLimitsKey_maxWorkspaceMembers',
  maxPendingInvites: 'NLSOwnerConsolePlanLimitsKey_maxPendingInvites',
  maxEnabledDashboardViews: 'NLSOwnerConsolePlanLimitsKey_maxEnabledDashboardViews',
  maxAiPromptCharacters: 'NLSOwnerConsolePlanLimitsKey_maxAiPromptCharacters',
}

const TIER_LABELS: Record<TierId, keyof typeof STRINGS_NLS> = {
  FREE_TIER: 'NLSOwnerConsolePlanLimitsTierFree',
  ESSENTIAL_TIER: 'NLSOwnerConsolePlanLimitsTierEssential',
  GROWTH_TIER: 'NLSOwnerConsolePlanLimitsTierGrowth',
  PROFESSIONAL_TIER: 'NLSOwnerConsolePlanLimitsTierProfessional',
  ENTERPRISE_TIER: 'NLSOwnerConsolePlanLimitsTierEnterprise',
}

export function OwnerPlanLimitsPanel(): ReactNode {
  const { t } = useNls()
  const { can } = useRoles()
  const canEdit = can('planLimits:edit')
  const overrides = usePlanLimitOverrides()

  const [isEditing, setIsEditing] = useState(false)
  const [selectedTier, setSelectedTier] = useState<TierId>('FREE_TIER')
  const [tierDraft, setTierDraft] = useState<TierDraft>({})
  const [previousNumbers, setPreviousNumbers] = useState<Record<string, string>>({})
  const [reason, setReason] = useState('')

  const [preview, setPreview] = useState<PlanLimitsPreview | null>(null)
  const [previewPending, setPreviewPending] = useState(false)
  const [applyPending, setApplyPending] = useState(false)
  const [frozenPayload, setFrozenPayload] = useState<PlanLimitOverrides | null>(null)
  const [previewDocVersion, setPreviewDocVersion] = useState<number | null>(null)
  const [isModifiedAfterPreview, setIsModifiedAfterPreview] = useState(false)
  const [appliedActionId, setAppliedActionId] = useState<string | null>(null)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  // Version staleness derived directly from live store version vs previewed version
  const isVersionStale = Boolean(
    preview !== null &&
    previewDocVersion !== null &&
    (overrides?.version ?? 0) !== previewDocVersion,
  )

  // Initialize draft when entering edit or switching tier
  const initTierDraft = (tier: TierId, current: PlanLimitOverrides | undefined) => {
    const existingTier = current?.tiers?.[tier]
    const initialDraft: TierDraft = {
      tierLimits: existingTier?.tierLimits ? { ...existingTier.tierLimits } : {},
      workspaceDefaults: existingTier?.workspaceDefaults ? { ...existingTier.workspaceDefaults } : {},
      workspaceCaps: existingTier?.workspaceCaps ? { ...existingTier.workspaceCaps } : {},
    }
    setTierDraft(initialDraft)

    const prev: Record<string, string> = {}
    if (existingTier?.tierLimits) {
      for (const [k, v] of Object.entries(existingTier.tierLimits)) {
        if (v !== undefined && v !== -1) prev[k] = String(v)
      }
    }
    setPreviousNumbers(prev)
  }

  const handleStartEdit = () => {
    setIsEditing(true)
    setAppliedActionId(null)
    setErrorMessage(null)
    setPreview(null)
    setFrozenPayload(null)
    setIsModifiedAfterPreview(false)
    initTierDraft(selectedTier, overrides)
  }

  const handleSelectTier = (nextTier: TierId) => {
    setSelectedTier(nextTier)
    setPreview(null)
    setFrozenPayload(null)
    setIsModifiedAfterPreview(false)
    initTierDraft(nextTier, overrides)
  }

  const handleCancelEdit = () => {
    setIsEditing(false)
    setPreview(null)
    setFrozenPayload(null)
    setIsModifiedAfterPreview(false)
  }

  // Edit actions on draft fields
  const markDirty = () => {
    if (preview !== null) {
      setPreview(null)
      setFrozenPayload(null)
      setIsModifiedAfterPreview(true)
    }
  }

  const handleToggleUnlimited = (key: FeatureKey, checked: boolean) => {
    markDirty()
    setTierDraft((prev) => {
      const nextLimits = { ...(prev.tierLimits || {}) }
      if (checked) {
        const current = nextLimits[key]
        if (current !== undefined && current !== -1 && String(current).trim() !== '') {
          setPreviousNumbers((p) => ({ ...p, [key]: String(current) }))
        }
        nextLimits[key] = -1
      } else {
        const restored = previousNumbers[key]
        if (restored !== undefined && restored.trim() !== '') {
          nextLimits[key] = restored
        } else {
          delete nextLimits[key]
        }
      }
      return { ...prev, tierLimits: nextLimits }
    })
  }

  const handleToggleUnlimitedWorkspaceCaps = (key: WorkspaceCountLimitKey, checked: boolean) => {
    markDirty()
    setTierDraft((prev) => {
      const nextCaps = { ...(prev.workspaceCaps || {}) }
      const prevKey = `workspaceCaps.${key}`
      if (checked) {
        const current = nextCaps[key]
        if (current !== undefined && current !== -1 && String(current).trim() !== '') {
          setPreviousNumbers((p) => ({ ...p, [prevKey]: String(current) }))
        }
        nextCaps[key] = -1
      } else {
        const restored = previousNumbers[prevKey]
        if (restored !== undefined && restored.trim() !== '') {
          nextCaps[key] = restored
        } else {
          delete nextCaps[key]
        }
      }
      return { ...prev, workspaceCaps: nextCaps }
    })
  }

  const handleUseCodeDefault = (section: 'tierLimits' | 'workspaceDefaults' | 'workspaceCaps', key: string) => {
    markDirty()
    setTierDraft((prev) => {
      const nextSection = { ...(prev[section] as Record<string, unknown>) }
      delete nextSection[key]
      return { ...prev, [section]: nextSection }
    })
    setPreviousNumbers((p) => {
      const next = { ...p }
      delete next[key]
      delete next[`${section}.${key}`]
      return next
    })
  }

  const handleInputChange = (
    section: 'tierLimits' | 'workspaceDefaults' | 'workspaceCaps',
    key: string,
    rawVal: string,
  ) => {
    markDirty()
    setTierDraft((prev) => {
      const nextSection = { ...(prev[section] as Record<string, unknown>) }
      const trimmed = rawVal.trim()
      if (trimmed === '') {
        delete nextSection[key]
      } else {
        nextSection[key] = rawVal
      }
      return { ...prev, [section]: nextSection }
    })

    if (section === 'tierLimits' && rawVal.trim() !== '' && !isNaN(Number(rawVal)) && Number(rawVal) !== -1) {
      setPreviousNumbers((p) => ({ ...p, [key]: rawVal }))
    }
  }

  // Field errors map for inline validation (F-PL2a-2)
  const fieldErrors = useMemo(() => {
    const errors: Record<string, string> = {}

    // Tier limits
    for (const featKey of FEATURE_KEYS) {
      const val = tierDraft.tierLimits?.[featKey]
      if (val !== undefined && val !== -1 && val !== '') {
        const str = String(val).trim()
        if (str !== '') {
          if (featKey === 'storageGB') {
            const num = Number(str)
            if (!Number.isFinite(num) || isNaN(num) || !/^-?\d+(\.\d+)?$/.test(str)) {
              errors[`tierLimits.${featKey}`] = t('NLSOwnerConsolePlanLimitsErrorMustBeNumber')
            } else if (num < 0 && num !== -1) {
              errors[`tierLimits.${featKey}`] = t('NLSOwnerConsolePlanLimitsErrorMustBeNonNegative')
            }
          } else {
            const num = Number(str)
            if (!Number.isInteger(num) || !/^-?\d+$/.test(str)) {
              errors[`tierLimits.${featKey}`] = t('NLSOwnerConsolePlanLimitsErrorMustBeInteger')
            } else if (num < 0 && num !== -1) {
              errors[`tierLimits.${featKey}`] = t('NLSOwnerConsolePlanLimitsErrorMustBeNonNegative')
            }
          }
        }
      }
    }

    // Workspace caps
    for (const capKey of WORKSPACE_COUNT_LIMIT_KEYS) {
      const val = tierDraft.workspaceCaps?.[capKey]
      if (val !== undefined && val !== '') {
        const str = String(val).trim()
        if (str !== '') {
          const num = Number(str)
          if (!Number.isInteger(num) || !/^-?\d+$/.test(str)) {
            errors[`workspaceCaps.${capKey}`] = t('NLSOwnerConsolePlanLimitsErrorMustBeInteger')
          }
        }
      }
    }

    // Workspace defaults
    for (const capKey of WORKSPACE_COUNT_LIMIT_KEYS) {
      const val = tierDraft.workspaceDefaults?.[capKey]
      if (val !== undefined && val !== '') {
        const str = String(val).trim()
        if (str !== '') {
          const num = Number(str)
          if (!Number.isInteger(num) || !/^-?\d+$/.test(str)) {
            errors[`workspaceDefaults.${capKey}`] = t('NLSOwnerConsolePlanLimitsErrorMustBeInteger')
          }
        }
      }
    }

    return errors
  }, [tierDraft, t])

  const hasFieldErrors = Object.keys(fieldErrors).length > 0

  const candidatePayload = useMemo(() => {
    return buildTierPayload(overrides, selectedTier, tierDraft)
  }, [overrides, selectedTier, tierDraft])

  const validationResult = useMemo(() => {
    return validatePlanLimitOverrides(candidatePayload)
  }, [candidatePayload])

  const reasonValid = reason.trim().length >= 6
  const canPreview = reasonValid && validationResult.ok && !hasFieldErrors && !previewPending

  // Run preview
  const handlePreview = async () => {
    if (!canPreview) return
    setPreviewPending(true)
    setErrorMessage(null)
    setIsModifiedAfterPreview(false)

    try {
      const currentVer = overrides?.version ?? 0
      const frozen = buildTierPayload(overrides, selectedTier, tierDraft)
      const res = await previewPlanLimits({
        planLimits: frozen,
        reason: reason.trim(),
      })
      setPreview(res)
      setPreviewDocVersion(currentVer)
      setFrozenPayload(frozen)
    } catch (err: unknown) {
      setErrorMessage(err instanceof Error ? err.message : t('NLSOwnerConsolePlanLimitsError'))
    } finally {
      setPreviewPending(false)
    }
  }

  // Run confirm & apply
  const handleConfirm = async () => {
    if (!preview || !frozenPayload || isVersionStale || applyPending) return
    setApplyPending(true)
    setErrorMessage(null)

    try {
      const res = await applyPlanLimits({
        planLimits: frozenPayload,
        reason: reason.trim(),
        previewToken: preview.previewToken,
      })
      if (res.ok && res.actionId) {
        setAppliedActionId(res.actionId)
        setIsEditing(false)
        setPreview(null)
        setFrozenPayload(null)
      } else {
        setErrorMessage(t('NLSOwnerConsolePlanLimitsApplyFailedNoActionId'))
      }
    } catch (err: unknown) {
      setErrorMessage(err instanceof Error ? err.message : t('NLSOwnerConsolePlanLimitsError'))
    } finally {
      setApplyPending(false)
    }
  }

  // Matrix data for read view
  const matrix = useMemo(() => buildMatrix(overrides), [overrides])

  const tierLimitsRows = useMemo(
    () => matrix.rows.filter((r) => r.section === 'tierLimits'),
    [matrix],
  )
  const workspaceDefaultsRows = useMemo(
    () => matrix.rows.filter((r) => r.section === 'workspaceDefaults'),
    [matrix],
  )
  const workspaceCapsRows = useMemo(
    () => matrix.rows.filter((r) => r.section === 'workspaceCaps'),
    [matrix],
  )

  // OwnerTable Column definitions
  const columns: ColumnDef<MatrixRow>[] = useMemo(() => [
    {
      key: 'resource',
      header: t('NLSOwnerConsolePlanLimitsColResource'),
      render: (row) => (
        <span className="ownerPlanLimitsResourceName">
          {row.section === 'tierLimits'
            ? t(FEATURE_KEY_LABELS[row.key as FeatureKey])
            : t(WORKSPACE_KEY_LABELS[row.key as WorkspaceCountLimitKey])}
        </span>
      ),
    },
    ...TIER_IDS.map((tier) => ({
      key: tier,
      header: t(TIER_LABELS[tier]),
      render: (row: MatrixRow) => {
        const cell = row.cells[tier]
        return (
          <div className="ownerPlanLimitsCell">
            <span className="ownerPlanLimitsEffectiveVal">
              {cell.effectiveValue === -1
                ? t('NLSOwnerConsolePlanLimitsUnlimited')
                : cell.effectiveValue}
            </span>
            {cell.isOverridden && (
              <div className="ownerPlanLimitsCellOverrideInfo">
                <span className="ownerPlanLimitsOverrideBadge">
                  {t('NLSOwnerConsolePlanLimitsOverridden')}
                </span>
                <span className="ownerPlanLimitsDefaultHint">
                  {t('NLSOwnerConsolePlanLimitsCodeDefault', {
                    value:
                      cell.codeDefault === -1
                        ? t('NLSOwnerConsolePlanLimitsUnlimited')
                        : String(cell.codeDefault),
                  })}
                </span>
              </div>
            )}
          </div>
        )
      },
    })),
  ], [t])

  const tierWorkspaceCount = preview?.tierWorkspaceCounts?.[selectedTier]

  const getDiffSectionName = (sec: string) => {
    if (sec === 'tierLimits') return t('NLSOwnerConsolePlanLimitsGroupTierLimits')
    if (sec === 'workspaceDefaults') return t('NLSOwnerConsolePlanLimitsGroupWorkspaceDefaults')
    if (sec === 'workspaceCaps') return t('NLSOwnerConsolePlanLimitsGroupWorkspaceCaps')
    return sec
  }

  const getDiffFieldLabel = (sec: string, key: string) => {
    if (sec === 'tierLimits') {
      const k = key as FeatureKey
      return FEATURE_KEY_LABELS[k] ? t(FEATURE_KEY_LABELS[k]) : key
    }
    const wk = key as WorkspaceCountLimitKey
    return WORKSPACE_KEY_LABELS[wk] ? t(WORKSPACE_KEY_LABELS[wk]) : key
  }

  const formatDiffVal = (val: number | string | undefined | null) => {
    if (val === -1) return t('NLSOwnerConsolePlanLimitsUnlimited')
    if (val === undefined || val === null) return ''
    return String(val)
  }

  return (
    <div className="ownerPlanLimitsPanel">
      {/* Header */}
      <header className="ownerPlanLimitsHeader">
        <div className="ownerPlanLimitsTitleArea">
          <h2 className="ownerPlanLimitsTitle">{t('NLSOwnerConsoleNavPlanLimits')}</h2>
          <p className="ownerPlanLimitsSubtitle">
            {t('NLSOwnerConsolePlanLimitsSubtitle')}
          </p>
          <p className="ownerPlanLimitsNote">
            {t('NLSOwnerConsolePlanLimitsNonEditableNote')}
          </p>
        </div>
        {!isEditing && canEdit && (
          <Button variant="primary" onClick={handleStartEdit}>
            {t('NLSOwnerConsolePlanLimitsEditBtn')}
          </Button>
        )}
      </header>

      {/* Success banner after apply */}
      {appliedActionId && !isEditing && (
        <div className="ownerPlanLimitsSuccessBanner" role="status">
          <span>
            {t('NLSOwnerConsolePlanLimitsSuccess', { actionId: appliedActionId })}
          </span>
          <Button
            variant="secondary"
            size="sm"
            onClick={() =>
              navigateOwner({
                view: 'activity-detail',
                actionId: appliedActionId,
              })
            }
          >
            {t('NLSOwnerConsolePlanLimitsViewActivity')}
          </Button>
        </div>
      )}

      {/* Error message */}
      {errorMessage && (
        <div className="ownerPlanLimitsErrorBanner" role="alert">
          {errorMessage}
        </div>
      )}

      {/* Edit View */}
      {isEditing ? (
        <div className="ownerPlanLimitsEditor">
          {/* Tier Chooser Toolbar */}
          <div className="ownerPlanLimitsEditorToolbar">
            <div className="ownerPlanLimitsTierSelector">
              <label id="plan-limits-tier-label" className="ownerPlanLimitsTierLabel">
                <strong>{t('NLSOwnerConsolePlanLimitsChooseTier')}:</strong>
              </label>
              <Select<TierId>
                value={selectedTier}
                options={TIER_IDS.map((tier) => ({
                  value: tier,
                  label: t(TIER_LABELS[tier]),
                }))}
                onChange={handleSelectTier}
                ariaLabel={t('NLSOwnerConsolePlanLimitsChooseTier')}
              />
            </div>
            <Button variant="secondary" size="sm" onClick={handleCancelEdit}>
              {t('NLSOwnerConsolePlanLimitsCancelBtn')}
            </Button>
          </div>

          {/* Section 1: Tier Limits */}
          <section className="ownerPlanLimitsSection">
            <h3 className="ownerPlanLimitsSectionTitle">
              {t('NLSOwnerConsolePlanLimitsGroupTierLimits')}
            </h3>
            <div className="ownerPlanLimitsFieldsGrid">
              {FEATURE_KEYS.map((featKey) => {
                const labelText = t(FEATURE_KEY_LABELS[featKey])
                const codeDefault = resolveTierLimit(selectedTier, featKey)
                const overrideVal = tierDraft.tierLimits?.[featKey]
                const isUnlimited = overrideVal === -1
                const inputVal =
                  overrideVal !== undefined && overrideVal !== -1
                    ? String(overrideVal)
                    : ''
                const fieldError = fieldErrors[`tierLimits.${featKey}`]

                return (
                  <div className="ownerPlanLimitsFieldCard" key={featKey}>
                    <div className="ownerPlanLimitsFieldHeader">
                      <span className="ownerPlanLimitsFieldLabel">{labelText}</span>
                      <div className="ownerPlanLimitsFieldMeta">
                        <span data-testid={`codeDefault-tierLimits-${featKey}`}>
                          {t('NLSOwnerConsolePlanLimitsCodeDefault', {
                            value:
                              codeDefault === -1
                                ? t('NLSOwnerConsolePlanLimitsUnlimited')
                                : String(codeDefault),
                          })}
                        </span>
                      </div>
                    </div>
                    <div className="ownerPlanLimitsFieldControls">
                      <Toggle
                        label={t('NLSOwnerConsolePlanLimitsToggleUnlimited')}
                        checked={isUnlimited}
                        onChange={(checked) => handleToggleUnlimited(featKey, Boolean(checked))}
                        id={`toggle-unlimited-${featKey}`}
                        aria-label={`${labelText} ${t('NLSOwnerConsolePlanLimitsToggleUnlimited')}`}
                        data-testid={`toggle-unlimited-${featKey}`}
                      />
                      <TextInput
                        inputMode={featKey === 'storageGB' ? 'decimal' : 'numeric'}
                        className="ownerPlanLimitsInput"
                        data-testid={`input-tierLimits-${featKey}`}
                        value={inputVal}
                        onChange={(next) => handleInputChange('tierLimits', featKey, next)}
                        placeholder={
                          codeDefault === -1
                            ? t('NLSOwnerConsolePlanLimitsUnlimited')
                            : String(codeDefault)
                        }
                        disabled={isUnlimited}
                        aria-label={labelText}
                        error={Boolean(fieldError)}
                      />
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => handleUseCodeDefault('tierLimits', featKey)}
                        data-testid={`revert-default-${featKey}`}
                      >
                        {t('NLSOwnerConsolePlanLimitsUseCodeDefault')}
                      </Button>
                      {fieldError && (
                        <span className="ownerPlanLimitsFieldError" role="alert">
                          {fieldError}
                        </span>
                      )}
                    </div>
                  </div>
                )
              })}
            </div>
          </section>

          {/* Section 2: Team Workspace Defaults */}
          <section className="ownerPlanLimitsSection">
            <h3 className="ownerPlanLimitsSectionTitle">
              {t('NLSOwnerConsolePlanLimitsGroupWorkspaceDefaults')}
            </h3>
            <div className="ownerPlanLimitsFieldsGrid">
              {WORKSPACE_COUNT_LIMIT_KEYS.map((capKey) => {
                const labelText = t(WORKSPACE_KEY_LABELS[capKey])
                const codeDefault = resolveTeamWorkspaceDefaults(selectedTier, undefined)[capKey]
                const effectiveCap = resolveTeamWorkspaceCaps(selectedTier, candidatePayload)[capKey]

                const overrideVal = tierDraft.workspaceDefaults?.[capKey]
                const inputVal = overrideVal !== undefined ? String(overrideVal) : ''
                const fieldError = fieldErrors[`workspaceDefaults.${capKey}`]

                return (
                  <div className="ownerPlanLimitsFieldCard" key={capKey}>
                    <div className="ownerPlanLimitsFieldHeader">
                      <span className="ownerPlanLimitsFieldLabel">{labelText}</span>
                      <div className="ownerPlanLimitsFieldMeta">
                        <span data-testid={`codeDefault-workspaceDefaults-${capKey}`}>
                          {t('NLSOwnerConsolePlanLimitsCodeDefault', {
                            value: String(codeDefault),
                          })}
                        </span>
                        <span data-testid={`ceiling-workspaceDefaults-${capKey}`}>
                          {t('NLSOwnerConsolePlanLimitsCeiling', {
                            value: String(effectiveCap),
                          })}
                        </span>
                      </div>
                    </div>
                    <div className="ownerPlanLimitsFieldControls">
                      <TextInput
                        inputMode="numeric"
                        className="ownerPlanLimitsInput"
                        data-testid={`input-workspaceDefaults-${capKey}`}
                        value={inputVal}
                        onChange={(next) =>
                          handleInputChange('workspaceDefaults', capKey, next)
                        }
                        placeholder={String(codeDefault)}
                        aria-label={labelText}
                        error={Boolean(fieldError)}
                      />
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => handleUseCodeDefault('workspaceDefaults', capKey)}
                      >
                        {t('NLSOwnerConsolePlanLimitsUseCodeDefault')}
                      </Button>
                      {fieldError && (
                        <span className="ownerPlanLimitsFieldError" role="alert">
                          {fieldError}
                        </span>
                      )}
                    </div>
                  </div>
                )
              })}
            </div>
          </section>

          {/* Section 3: Team Workspace Caps */}
          <section className="ownerPlanLimitsSection">
            <h3 className="ownerPlanLimitsSectionTitle">
              {t('NLSOwnerConsolePlanLimitsGroupWorkspaceCaps')}
            </h3>
            <div className="ownerPlanLimitsFieldsGrid">
              {WORKSPACE_COUNT_LIMIT_KEYS.map((capKey) => {
                const labelText = t(WORKSPACE_KEY_LABELS[capKey])
                const codeDefault = resolveTeamWorkspaceCaps(selectedTier, undefined)[capKey]
                const hardCap = WORKSPACE_LIMIT_HARD_CAPS[capKey]
                const overrideVal = tierDraft.workspaceCaps?.[capKey]
                const isUnlimited = overrideVal === -1
                const inputVal =
                  overrideVal !== undefined && overrideVal !== -1
                    ? String(overrideVal)
                    : ''
                const fieldError = fieldErrors[`workspaceCaps.${capKey}`]

                return (
                  <div className="ownerPlanLimitsFieldCard" key={capKey}>
                    <div className="ownerPlanLimitsFieldHeader">
                      <span className="ownerPlanLimitsFieldLabel">{labelText}</span>
                      <div className="ownerPlanLimitsFieldMeta">
                        <span data-testid={`codeDefault-workspaceCaps-${capKey}`}>
                          {t('NLSOwnerConsolePlanLimitsCodeDefault', {
                            value:
                              codeDefault === -1
                                ? t('NLSOwnerConsolePlanLimitsUnlimited')
                                : String(codeDefault),
                          })}
                        </span>
                        <span data-testid={`ceiling-workspaceCaps-${capKey}`}>
                          {t('NLSOwnerConsolePlanLimitsCeiling', {
                            value: String(hardCap),
                          })}
                        </span>
                      </div>
                    </div>
                    <div className="ownerPlanLimitsFieldControls">
                      <Toggle
                        label={t('NLSOwnerConsolePlanLimitsToggleUnlimited')}
                        checked={isUnlimited}
                        onChange={(checked) => handleToggleUnlimitedWorkspaceCaps(capKey, Boolean(checked))}
                        id={`toggle-unlimited-workspaceCaps-${capKey}`}
                        aria-label={`${labelText} ${t('NLSOwnerConsolePlanLimitsToggleUnlimited')}`}
                        data-testid={`toggle-unlimited-workspaceCaps-${capKey}`}
                      />
                      <TextInput
                        inputMode="numeric"
                        className="ownerPlanLimitsInput"
                        data-testid={`input-workspaceCaps-${capKey}`}
                        value={inputVal}
                        onChange={(next) =>
                          handleInputChange('workspaceCaps', capKey, next)
                        }
                        placeholder={
                          codeDefault === -1
                            ? t('NLSOwnerConsolePlanLimitsUnlimited')
                            : String(codeDefault)
                        }
                        disabled={isUnlimited}
                        aria-label={labelText}
                        error={Boolean(fieldError)}
                      />
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => handleUseCodeDefault('workspaceCaps', capKey)}
                        data-testid={`revert-default-workspaceCaps-${capKey}`}
                      >
                        {t('NLSOwnerConsolePlanLimitsUseCodeDefault')}
                      </Button>
                      {fieldError && (
                        <span className="ownerPlanLimitsFieldError" role="alert">
                          {fieldError}
                        </span>
                      )}
                    </div>
                  </div>
                )
              })}
            </div>
          </section>

          {/* Save / Preview Section */}
          <section className="ownerPlanLimitsSaveSection">
            <div className="ownerPlanLimitsReasonContainer">
              <label htmlFor="plan-limits-reason-input" className="ownerPlanLimitsReasonLabel">
                {t('NLSOwnerConsolePlanLimitsReasonLabel')}
              </label>
              <Textarea
                id="plan-limits-reason-input"
                className="ownerPlanLimitsReasonTextarea"
                data-testid="plan-limits-reason-input"
                value={reason}
                onChange={(next) => {
                  markDirty()
                  setReason(next)
                }}
                placeholder={t('NLSOwnerConsolePlanLimitsReasonPlaceholder')}
                aria-label={t('NLSOwnerConsolePlanLimitsReasonLabel')}
                rows={3}
                error={reason.length > 0 && !reasonValid}
              />
              {reason.length > 0 && !reasonValid && (
                <span className="ownerPlanLimitsFieldError">
                  {t('NLSOwnerConsolePlanLimitsReasonError')}
                </span>
              )}
            </div>

            {/* Inline candidate validation errors (overall doc) */}
            {!validationResult.ok && (
              <div className="ownerPlanLimitsValidationErrors" role="alert">
                <ul>
                  {validationResult.errors.map((err, i) => (
                    <li key={i}>{err}</li>
                  ))}
                </ul>
              </div>
            )}

            <div className="ownerPlanLimitsPreviewActions">
              <Button
                variant="secondary"
                disabled={!canPreview}
                onClick={handlePreview}
              >
                {previewPending
                  ? t('NLSOwnerConsolePlanLimitsPreviewingBtn')
                  : t('NLSOwnerConsolePlanLimitsPreviewBtn')}
              </Button>
            </div>

            {/* Warning if modified after preview */}
            {isModifiedAfterPreview && (
              <div className="ownerPlanLimitsWarnBanner" role="status">
                {t('NLSOwnerConsolePlanLimitsRePreviewRequired')}
              </div>
            )}

            {/* Error if store version changed */}
            {isVersionStale && (
              <div className="ownerPlanLimitsErrorBanner" role="alert">
                {t('NLSOwnerConsolePlanLimitsStaleVersion')}
              </div>
            )}

            {/* Preview Results & Confirm */}
            {preview && !isVersionStale && !isModifiedAfterPreview && (
              <div className="ownerPlanLimitsPreviewResults">
                <h4 className="ownerPlanLimitsDiffsTitle">
                  {t('NLSOwnerConsolePlanLimitsDiffsTitle')}
                </h4>

                {/* Workspace count */}
                <div className="ownerPlanLimitsWorkspaceCount">
                  {t('NLSOwnerConsolePlanLimitsWorkspaceCount', {
                    count:
                      tierWorkspaceCount === null || tierWorkspaceCount === undefined
                        ? t('NLSOwnerConsolePlanLimitsCouldNotCount')
                        : String(tierWorkspaceCount),
                  })}
                </div>

                {/* Server note */}
                {preview.note && (
                  <div className="ownerPlanLimitsServerNote">
                    {t('NLSOwnerConsolePlanLimitsServerNote', { note: preview.note })}
                  </div>
                )}

                {/* Diffs list */}
                {preview.diffs.length === 0 ? (
                  <p className="ownerPlanLimitsNoDiffs">
                    {t('NLSOwnerConsolePlanLimitsNoChanges')}
                  </p>
                ) : (
                  <ul className="ownerPlanLimitsDiffList">
                    {preview.diffs.map((d, idx) => (
                      <li key={idx} className="ownerPlanLimitsDiffItem">
                        <span className="ownerPlanLimitsDiffField">
                          {getDiffSectionName(d.section)} &rsaquo; {getDiffFieldLabel(d.section, d.key)}
                        </span>
                        <span className="ownerPlanLimitsDiffValues">
                          {formatDiffVal(d.before)} &rarr; {formatDiffVal(d.after)}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}

                <div className="ownerPlanLimitsConfirmActions">
                  <Button
                    variant="primary"
                    disabled={applyPending || isVersionStale}
                    onClick={handleConfirm}
                  >
                    {applyPending
                      ? t('NLSOwnerConsolePlanLimitsApplyingBtn')
                      : t('NLSOwnerConsolePlanLimitsConfirmBtn')}
                  </Button>
                </div>
              </div>
            )}
          </section>
        </div>
      ) : (
        /* Read View: 3 OwnerTable instances under group headings */
        <div className="ownerPlanLimitsTablesContainer">
          <section className="ownerPlanLimitsTableSection">
            <h3 className="ownerPlanLimitsGroupTitle">
              {t('NLSOwnerConsolePlanLimitsGroupTierLimits')}
            </h3>
            <OwnerTable<MatrixRow>
              data={tierLimitsRows}
              columns={columns}
              getRowId={(row) => row.key}
              viewKey="plan-limits-tier-limits"
              className="ownerPlanLimitsTable"
            />
          </section>

          <section className="ownerPlanLimitsTableSection">
            <h3 className="ownerPlanLimitsGroupTitle">
              {t('NLSOwnerConsolePlanLimitsGroupWorkspaceDefaults')}
            </h3>
            <OwnerTable<MatrixRow>
              data={workspaceDefaultsRows}
              columns={columns}
              getRowId={(row) => row.key}
              viewKey="plan-limits-workspace-defaults"
              className="ownerPlanLimitsTable"
            />
          </section>

          <section className="ownerPlanLimitsTableSection">
            <h3 className="ownerPlanLimitsGroupTitle">
              {t('NLSOwnerConsolePlanLimitsGroupWorkspaceCaps')}
            </h3>
            <OwnerTable<MatrixRow>
              data={workspaceCapsRows}
              columns={columns}
              getRowId={(row) => row.key}
              viewKey="plan-limits-workspace-caps"
              className="ownerPlanLimitsTable"
            />
          </section>
        </div>
      )}
    </div>
  )
}
